import { syncOfflinePosSales } from "../api/offlinePos";
import { concreteIdentityKey } from "../utils/productVariants";
import {
  commitOfflineSaleTransaction,
  commitOfflineSyncResults,
  getOfflineSaleRecordsByScope,
  listOfflineOutboxByScope,
  markOfflineOutboxSyncing,
  restoreOfflineOutboxPending,
} from "./db";
import {
  getOfflineDevice,
  getOfflinePosSession,
  isOfflineGrantUsable,
  offlineScope,
} from "./session";

export const OFFLINE_SALE_STATUS = Object.freeze({
  PENDING: "PENDING",
  SYNCING: "SYNCING",
  SYNCED: "SYNCED",
  RECONCILIATION_REQUIRED: "RECONCILIATION_REQUIRED",
  FAILED_PERMANENTLY: "FAILED_PERMANENTLY",
});

const SYNC_BATCH_LIMIT = 25;
const RECENT_SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;
const TERMINAL_SYNC_STATUSES = new Set([
  OFFLINE_SALE_STATUS.SYNCED,
  OFFLINE_SALE_STATUS.RECONCILIATION_REQUIRED,
  OFFLINE_SALE_STATUS.FAILED_PERMANENTLY,
]);
const syncInFlightByScope = new Map();

function notifyOfflineSaleUpdated(scope) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:sale-updated", { detail: { scope } }));
  }
}

function newOfflineTransactionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `opst_${crypto.randomUUID()}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const value = [...bytes].map((item) => item.toString(16).padStart(2, "0")).join("");
    return `opst_${value}`;
  }
  throw new Error("This browser cannot generate a secure Offline POS transaction ID.");
}

function moneyString(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error("Offline sale contains an invalid price.");
  }
  return amount.toFixed(2);
}

function localInvoiceNumber(transactionId, soldAt) {
  const date = new Date(soldAt);
  const day = Number.isNaN(date.getTime())
    ? "LOCAL"
    : date.toISOString().slice(0, 10).replaceAll("-", "");
  return `OFFLINE-${day}-${transactionId.slice(-8).toUpperCase()}`;
}

function requireOfflineSaleCart(cart, snapshotMeta) {
  if (!snapshotMeta?.snapshot_id || Number(snapshotMeta.schema_version) !== 2) {
    throw new Error("A current trusted Offline POS snapshot is required before selling offline.");
  }
  if (!Array.isArray(cart) || cart.length === 0) {
    throw new Error("Add at least one product before completing the sale.");
  }
  for (const line of cart) {
    if (!line.product_public_id || !line.sale_claim) {
      throw new Error("The cart contains an item that is not authorized for offline sale.");
    }
    const quantity = Number(line.quantity || 0);
    const available = Number(line.available_quantity || 0);
    if (!Number.isInteger(quantity) || quantity <= 0 || quantity > 1_000_000) {
      throw new Error("Offline sale contains an invalid quantity.");
    }
    if (line.track_inventory !== false && quantity > available) {
      throw new Error(`Offline stock changed for ${line.product_name || "a product"}. Refresh the cart and try again.`);
    }
  }
}

export async function completeOfflineCashSale({
  workspacePublicId,
  storePublicId,
  snapshotMeta,
  cart,
}) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) throw new Error("Workspace and store are required for Offline POS.");
  requireOfflineSaleCart(cart, snapshotMeta);

  const [device, session] = await Promise.all([
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  const cashierUserPublicId = session?.identity?.user?.public_id;
  if (
    !device?.device_public_id
    || device.status !== "active"
    || !session
    || session.device_public_id !== device.device_public_id
    || snapshotMeta.device_public_id !== device.device_public_id
    || session.identity?.workspace?.public_id !== workspacePublicId
    || session.identity?.store?.public_id !== storePublicId
    || !cashierUserPublicId
    || !isOfflineGrantUsable(session)
  ) {
    throw new Error("Offline POS authorization is no longer valid for this sale.");
  }

  const offlineTransactionId = newOfflineTransactionId();
  const soldAt = new Date().toISOString();
  const items = cart.map((line) => ({
    product_public_id: line.product_public_id,
    product_variant_public_id: line.product_variant_public_id || null,
    product_name: line.product_name,
    sku: line.sku || null,
    category: line.category || "Uncategorized",
    variant_label: line.variant_label || null,
    variant_options: line.variant_options || null,
    quantity: Number(line.quantity),
    unit_price: moneyString(line.price),
    sale_claim: line.sale_claim,
    track_inventory: line.track_inventory !== false,
    local_physical_quantity: line.track_inventory === false ? null : Math.max(0, Number(line.physical_quantity || 0)),
    local_reserved_quantity: line.track_inventory === false ? null : Math.max(0, Number(line.reserved_quantity || 0)),
    local_available_quantity: line.track_inventory === false ? null : Math.max(0, Number(line.available_quantity || 0)),
  }));
  const total = items.reduce(
    (sum, item) => sum + Number(item.unit_price) * item.quantity,
    0,
  );
  const transaction = {
    offline_transaction_id: offlineTransactionId,
    snapshot_id: snapshotMeta.snapshot_id,
    sold_at: soldAt,
    payment_method: "cash",
    items,
  };
  const invoiceNumber = localInvoiceNumber(offlineTransactionId, soldAt);
  const receiptItems = items.map((item) => ({
    product_public_id: item.product_public_id,
    product_variant_public_id: item.product_variant_public_id,
    variant_label: item.variant_label,
    variant_options: item.variant_options,
    product_name: item.product_name,
    sku: item.sku,
    quantity: item.quantity,
    unit_price: item.unit_price,
    line_total: moneyString(Number(item.unit_price) * item.quantity),
  }));
  const receipt = {
    offline_transaction_id: offlineTransactionId,
    sale_public_id: offlineTransactionId,
    invoice_number: invoiceNumber,
    source: "offline_pos_pending",
    status: "completed",
    currency: "USD",
    subtotal: moneyString(total),
    discount_total: "0.00",
    tax_total: "0.00",
    other_total: "0.00",
    total_amount: moneyString(total),
    payment_status: "paid",
    sold_at: soldAt,
    items: receiptItems,
    payments: [{ method: "cash", amount: moneyString(total), status: "completed", paid_at: soldAt }],
    offline_pending: true,
    sync_status: OFFLINE_SALE_STATUS.PENDING,
  };
  const now = new Date().toISOString();
  const saleRecord = {
    offline_transaction_id: offlineTransactionId,
    scope,
    workspace_public_id: workspacePublicId,
    store_public_id: storePublicId,
    device_public_id: snapshotMeta.device_public_id,
    cashier_user_public_id: cashierUserPublicId,
    snapshot_id: snapshotMeta.snapshot_id,
    status: OFFLINE_SALE_STATUS.PENDING,
    transaction,
    provisional_receipt: receipt,
    created_at: now,
    updated_at: now,
    canonical_sale_public_id: null,
    canonical_invoice_number: null,
    reconciliation: [],
  };
  const outboxRecord = {
    offline_transaction_id: offlineTransactionId,
    scope,
    device_public_id: snapshotMeta.device_public_id,
    cashier_user_public_id: cashierUserPublicId,
    status: OFFLINE_SALE_STATUS.PENDING,
    transaction,
    created_at: now,
    sync_attempts: 0,
    last_sync_attempt_at: null,
    last_sync_error: null,
  };
  const inventoryDeductions = cart.filter((line) => line.track_inventory !== false).map((line) => ({
    key: `${scope}:inventory:${concreteIdentityKey(line.product_public_id, line.product_variant_public_id)}`,
    snapshot_id: snapshotMeta.snapshot_id,
    quantity: Number(line.quantity),
  }));

  await commitOfflineSaleTransaction({ saleRecord, outboxRecord, inventoryDeductions });
  notifyOfflineSaleUpdated(scope);
  return receipt;
}

function validateSyncAcknowledgements(results, attemptedIds) {
  if (!Array.isArray(results)) {
    throw new Error("Offline sale sync returned an invalid acknowledgement payload.");
  }
  const attempted = new Set(attemptedIds);
  const seen = new Set();
  for (const result of results) {
    const transactionId = result?.offline_transaction_id;
    if (
      !transactionId
      || !attempted.has(transactionId)
      || seen.has(transactionId)
      || !TERMINAL_SYNC_STATUSES.has(result?.status)
    ) {
      throw new Error("Offline sale sync returned an invalid transaction acknowledgement.");
    }
    seen.add(transactionId);
  }
  return results;
}

async function syncPendingOfflineSalesOnce({
  workspacePublicId,
  storePublicId,
  options = {},
}) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return { attempted: 0, acknowledged: 0, results: [] };

  const [device, session] = await Promise.all([
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  if (!device?.device_public_id || !device?.device_secret || device.status !== "active") {
    throw new Error("Offline POS device authorization is unavailable for pending sales.");
  }
  if (
    !session
    || session.device_public_id !== device.device_public_id
    || !session.identity?.user?.public_id
    || session.identity?.workspace?.public_id !== workspacePublicId
    || session.identity?.store?.public_id !== storePublicId
    || !isOfflineGrantUsable(session)
  ) {
    throw new Error("Offline POS authorization must be refreshed before pending sales can sync.");
  }
  const outbox = await listOfflineOutboxByScope(
    scope,
    SYNC_BATCH_LIMIT,
    session.identity.user.public_id,
    device.device_public_id,
  );
  if (!outbox.length) return { attempted: 0, acknowledged: 0, results: [] };

  const ids = outbox.map((record) => record.offline_transaction_id);
  await markOfflineOutboxSyncing(scope, ids);
  notifyOfflineSaleUpdated(scope);
  try {
    const response = await syncOfflinePosSales(
      outbox.map((record) => record.transaction),
      device.device_public_id,
      device.device_secret,
      session.grant,
      options,
    );
    const results = validateSyncAcknowledgements(response.data?.results, ids);
    await commitOfflineSyncResults(scope, results);
    const acknowledgedIds = new Set(results.map((result) => result.offline_transaction_id));
    const unacknowledgedIds = ids.filter((id) => !acknowledgedIds.has(id));
    if (unacknowledgedIds.length) {
      await restoreOfflineOutboxPending(
        scope,
        unacknowledgedIds,
        "Server response did not acknowledge this offline sale. It will be retried safely.",
      );
    }
    if (results.length || unacknowledgedIds.length) notifyOfflineSaleUpdated(scope);
    const synced = results.filter((result) => result.status === OFFLINE_SALE_STATUS.SYNCED).length;
    const reconciliationRequired = results.filter(
      (result) => result.status === OFFLINE_SALE_STATUS.RECONCILIATION_REQUIRED,
    ).length;
    const failedPermanently = results.filter(
      (result) => result.status === OFFLINE_SALE_STATUS.FAILED_PERMANENTLY,
    ).length;
    return {
      attempted: outbox.length,
      acknowledged: results.length,
      results,
      synced,
      reconciliationRequired,
      failedPermanently,
      remainingInBatch: unacknowledgedIds.length,
    };
  } catch (error) {
    await restoreOfflineOutboxPending(
      scope,
      ids,
      error?.message || "Offline sale sync request failed.",
    );
    notifyOfflineSaleUpdated(scope);
    throw error;
  }
}

export function syncPendingOfflineSales(args) {
  const scope = offlineScope(args?.workspacePublicId, args?.storePublicId);
  if (!scope) return Promise.resolve({ attempted: 0, acknowledged: 0, results: [] });
  const existing = syncInFlightByScope.get(scope);
  if (existing) return existing;
  const promise = syncPendingOfflineSalesOnce(args)
    .finally(() => {
      if (syncInFlightByScope.get(scope) === promise) syncInFlightByScope.delete(scope);
    });
  syncInFlightByScope.set(scope, promise);
  return promise;
}

export async function getOfflineSaleOperationalStatus(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) {
    return {
      sales: [],
      lastSuccessfulSyncAt: null,
      lastAcknowledgedSyncAt: null,
      deviceMismatchedPending: 0,
      cashierMismatchedPending: 0,
    };
  }
  const [sales, device, session] = await Promise.all([
    getOfflineSaleRecordsByScope(scope),
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  const currentCashierPublicId = session?.identity?.user?.public_id || null;
  const visible = sales
    .filter((sale) => [
      OFFLINE_SALE_STATUS.PENDING,
      OFFLINE_SALE_STATUS.SYNCING,
      OFFLINE_SALE_STATUS.RECONCILIATION_REQUIRED,
      OFFLINE_SALE_STATUS.FAILED_PERMANENTLY,
    ].includes(sale.status))
    .map((sale) => ({
      ...sale,
      device_mismatch: Boolean(
        device?.device_public_id
        && sale.device_public_id
        && sale.device_public_id !== device.device_public_id,
      ),
      cashier_mismatch: Boolean(
        currentCashierPublicId
        && sale.cashier_user_public_id
        && sale.cashier_user_public_id !== currentCashierPublicId,
      ),
    }))
    .sort((left, right) => (
      String(right.updated_at || right.created_at)
        .localeCompare(String(left.updated_at || left.created_at))
    ));
  const acknowledged = sales
    .filter((sale) => sale.synced_at)
    .sort((left, right) => String(right.synced_at).localeCompare(String(left.synced_at)));
  const successful = acknowledged.filter((sale) => sale.status === OFFLINE_SALE_STATUS.SYNCED);
  return {
    sales: visible,
    lastSuccessfulSyncAt: successful[0]?.synced_at || null,
    lastAcknowledgedSyncAt: acknowledged[0]?.synced_at || null,
    deviceMismatchedPending: visible.filter((sale) => (
      sale.device_mismatch
      && [OFFLINE_SALE_STATUS.PENDING, OFFLINE_SALE_STATUS.SYNCING].includes(sale.status)
    )).length,
    cashierMismatchedPending: visible.filter((sale) => (
      sale.cashier_mismatch
      && [OFFLINE_SALE_STATUS.PENDING, OFFLINE_SALE_STATUS.SYNCING].includes(sale.status)
    )).length,
  };
}

export async function getOfflineSaleQueueSummary(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) {
    return {
      pending: 0,
      syncing: 0,
      reconciliation: 0,
      failed: 0,
      syncedRecently: 0,
      total: 0,
    };
  }
  const sales = await getOfflineSaleRecordsByScope(scope);
  const recentCutoff = Date.now() - RECENT_SYNC_WINDOW_MS;
  return {
    pending: sales.filter((sale) => sale.status === OFFLINE_SALE_STATUS.PENDING).length,
    syncing: sales.filter((sale) => sale.status === OFFLINE_SALE_STATUS.SYNCING).length,
    reconciliation: sales.filter((sale) => sale.status === OFFLINE_SALE_STATUS.RECONCILIATION_REQUIRED).length,
    failed: sales.filter((sale) => sale.status === OFFLINE_SALE_STATUS.FAILED_PERMANENTLY).length,
    syncedRecently: sales.filter((sale) => (
      sale.status === OFFLINE_SALE_STATUS.SYNCED
      && Number.isFinite(new Date(sale.synced_at || 0).getTime())
      && new Date(sale.synced_at).getTime() >= recentCutoff
    )).length,
    total: sales.length,
  };
}
